type Transformer = (key: string) => string;

/**
 * Property names whose direct child object is a map keyed by user data
 * (e.g. indicator `technical_label`s). When walking inside one of these,
 * the immediate level of keys is preserved verbatim; recursion continues
 * normally underneath each value.
 *
 * Shared by the case-transform interceptor and the indicator batch mutations,
 * which snake_case their multipart `operations` part themselves (FormData
 * bodies bypass the interceptor).
 */
export const MAP_KEY_NAMES = new Set([
  'form_sections_config',
  'formSectionsConfig',
  'association_sections_config',
  'associationSectionsConfig',
  'indicators',
  'children',
]);

/**
 * Walk through an object and transform its children using the provided `convert` method.
 */
export const transform = (
  value: unknown,
  convert: Transformer,
  preserveChildrenKeys: Set<string> = new Set(),
  preserveKeys = false,
): unknown => {
  if (value === null || value === undefined) return value;
  // Arrays under a `preserveChildrenKeys` parent are NOT key-dicts; their elements get full camelization.
  if (Array.isArray(value)) return value.map((item) => transform(item, convert, preserveChildrenKeys, false));
  if (typeof value !== 'object') return value;

  // Plain objects only — leave Date, Blob, File, FormData, etc. alone.
  const proto: unknown = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;

  const out: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const newKey = preserveKeys ? key : convert(key);
    const childPreserves = preserveChildrenKeys.has(key) || preserveChildrenKeys.has(newKey);
    out[newKey] = transform(child, convert, preserveChildrenKeys, childPreserves);
  }
  return out;
};
